/**
 * ── O FORMATO DO CARTÃO, POR PRATELEIRA (bancada, leva S4) ───────────────────
 *
 * O dono: «escolher o formato do cartão: quadrada, arredondada, bem
 * arredondada, círculo; borda nenhuma/fina/média/grossa; sombra nenhuma/suave/
 * forte». Três eixos por prateleira, cada um com «herdar» (`null`): o raio do
 * tema da área, sem borda, sem sombra — toda prateleira antiga continua igual.
 *
 * Sem import: o aluno lê. O que o aluno recebe são VARIÁVEIS CSS na `<section>`
 * da prateleira (`variaveisDoCartao`); o cartão lê `var(--cartao-raio,
 * var(--raio))` e companhia — quando os três são nulos, nada é emitido e o
 * cartão fica exatamente como era.
 */
export const FORMATOS_DE_CARTAO = ['quadrado', 'arredondado', 'bem_arredondado', 'circulo'] as const;
export type FormatoDeCartao = (typeof FORMATOS_DE_CARTAO)[number];
export const BORDAS_DE_CARTAO = ['nenhuma', 'fina', 'media', 'grossa'] as const;
export type BordaDeCartao = (typeof BORDAS_DE_CARTAO)[number];
export const SOMBRAS_DE_CARTAO = ['nenhuma', 'suave', 'forte'] as const;
export type SombraDeCartao = (typeof SOMBRAS_DE_CARTAO)[number];

export interface CartaoGravado {
  formato: FormatoDeCartao | null;
  borda: BordaDeCartao | null;
  sombra: SombraDeCartao | null;
}

export const ROTULO_DO_FORMATO: Record<FormatoDeCartao, string> = {
  quadrado: 'quadrada',
  arredondado: 'arredondada',
  bem_arredondado: 'bem arredondada',
  circulo: 'círculo',
};
export const ROTULO_DA_BORDA: Record<BordaDeCartao, string> = { nenhuma: 'nenhuma', fina: 'fina', media: 'média', grossa: 'grossa' };
export const ROTULO_DA_SOMBRA: Record<SombraDeCartao, string> = { nenhuma: 'nenhuma', suave: 'suave', forte: 'forte' };

const RAIO: Record<FormatoDeCartao, string> = {
  quadrado: '0px',
  arredondado: 'var(--raio)',
  bem_arredondado: 'calc(var(--raio) * 2.5)',
  circulo: '50%',
};
const BORDA: Record<BordaDeCartao, string> = {
  nenhuma: 'none',
  fina: '1px solid color-mix(in srgb, var(--texto) 18%, transparent)',
  media: '2px solid color-mix(in srgb, var(--texto) 22%, transparent)',
  grossa: '3px solid color-mix(in srgb, var(--texto) 28%, transparent)',
};
const SOMBRA: Record<SombraDeCartao, string> = {
  nenhuma: 'none',
  suave: '0 6px 18px rgba(0, 0, 0, 0.25)',
  forte: '0 14px 36px rgba(0, 0, 0, 0.45)',
};

/** As variáveis para a `<section>` da prateleira; `undefined` quando os três herdam (nenhum `style`). */
export function variaveisDoCartao(c: CartaoGravado | null | undefined): Record<string, string> | undefined {
  if (!c || (c.formato === null && c.borda === null && c.sombra === null)) return undefined;
  const v: Record<string, string> = {};
  if (c.formato !== null && c.formato in RAIO) v['--cartao-raio'] = RAIO[c.formato];
  if (c.borda !== null && c.borda in BORDA) v['--cartao-borda'] = BORDA[c.borda];
  if (c.sombra !== null && c.sombra in SOMBRA) v['--cartao-sombra'] = SOMBRA[c.sombra];
  return Object.keys(v).length === 0 ? undefined : v;
}

/** «Tudo herdado?» — para o payload não mandar `cartao` à toa. */
export function cartaoHerdado(c: CartaoGravado): boolean {
  return c.formato === null && c.borda === null && c.sombra === null;
}
